import {
  comparePlanogramGroupsBySlotNumber,
  earliestPlanogramSlotNumber,
  planogramMarginRagSortRank,
  resolvePlanogramMarginPercent,
  sortPlanogramDisplaySlots,
} from './planogramSort';

export interface PlanogramIncomeSlotInput {
  id: string;
  slotNumber: number;
  slotLabel: string | null;
  maxCapacity: number;
  listingFee: number;
  productId: string | null;
  productName: string | null;
  product?: {
    sku: string;
    name: string;
    imageUrl: string | null;
    media: Array<{ url: string; mediaType: string }> | null;
  } | null;
  effectiveSell: number | null;
  cost: number | null;
}

export interface PlanogramIncomeSlotLine {
  slotId: string;
  slotNumber: number;
  slotLabel: string | null;
  productId: string;
  productName: string | null;
  product: NonNullable<PlanogramIncomeSlotInput['product']> | null;
  maxCapacity: number;
  salePrice: number | null;
  costPrice: number | null;
  marginPercent: number | null;
  profitSellThrough: number | null;
  listingFeeMonthly: number;
}

export interface PlanogramIncomeProductGroup {
  key: string;
  productId: string;
  productName: string;
  product: NonNullable<PlanogramIncomeSlotInput['product']> | null;
  lines: PlanogramIncomeSlotLine[];
  totalCapacity: number;
  salePriceLabel: string;
  costPriceLabel: string;
  listingFeeLabel: string;
  blendedMargin: number | null;
  profitSellThrough: number;
  listingFeesMonthly: number;
  sortRank: number;
}

export interface PlanogramIncomeSummary {
  filledSlots: number;
  slotsMissingCost: number;
  /** Venue listing fees — calendar month, per filled slot. */
  listingFeesMonthly: number;
  /** Sales revenue if every unit sells once. */
  revenueSellThrough: number;
  /** Gross profit if every unit sells once. */
  profitSellThrough: number;
  monthlyCogs: number;
  /** Unweighted average of per-line margins (same as the Planogram tab). */
  blendedMargin: number | null;
}

export interface PlanogramIncomeOptions {
  monthlyCogs?: number;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function lineMarginPercent(cost: number, sellPrice: number): number | null {
  if (sellPrice <= 0 || cost <= 0) return null;
  return Math.round(((sellPrice - cost) / sellPrice) * 100 * 100) / 100;
}

export function uniformPlanogramNumberLabel(
  values: Array<number | null>,
  format: (n: number) => string
): string {
  const defined = values.filter((v): v is number => v != null);
  if (defined.length === 0) return '—';
  const first = defined[0];
  if (defined.every((v) => v === first)) return format(first);
  return 'Varies';
}

export function formatPlanogramGbp(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

export function formatPlanogramPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

/** Blended margin = unweighted average of per-line margins. */
export function computeBlendedMargin(margins: Array<number | null>): number | null {
  const valid = margins.filter((m): m is number => m != null);
  if (valid.length === 0) return null;
  return Math.round((valid.reduce((a, b) => a + b, 0) / valid.length) * 100) / 100;
}

function groupPlanogramIncomeLines(lines: PlanogramIncomeSlotLine[]): PlanogramIncomeProductGroup[] {
  const map = new Map<string, PlanogramIncomeProductGroup>();

  for (const line of lines) {
    const existing = map.get(line.productId);
    if (existing) {
      existing.lines.push(line);
      existing.totalCapacity += line.maxCapacity;
      existing.profitSellThrough += line.profitSellThrough ?? 0;
      existing.listingFeesMonthly += line.listingFeeMonthly;
    } else {
      map.set(line.productId, {
        key: line.productId,
        productId: line.productId,
        productName: line.productName?.trim() || 'Unknown product',
        product: line.product,
        lines: [line],
        totalCapacity: line.maxCapacity,
        salePriceLabel: '—',
        costPriceLabel: '—',
        listingFeeLabel: '—',
        blendedMargin: null,
        profitSellThrough: line.profitSellThrough ?? 0,
        listingFeesMonthly: line.listingFeeMonthly,
        sortRank: planogramMarginRagSortRank(null),
      });
    }
  }

  const groups = Array.from(map.values()).map((group) => {
    const sortedLines = [...group.lines].sort((a, b) => a.slotNumber - b.slotNumber);
    const margins = sortedLines.map((line) => line.marginPercent);
    const worstRank = Math.min(
      ...sortedLines.map((line) =>
        planogramMarginRagSortRank(
          resolvePlanogramMarginPercent({
            slotNumber: line.slotNumber,
            slotLabel: line.slotLabel,
            productId: line.productId,
            marginPercent: line.marginPercent,
          })
        )
      )
    );

    return {
      ...group,
      product: sortedLines[0]?.product ?? null,
      lines: sortedLines,
      profitSellThrough: roundMoney(
        sortedLines.reduce((sum, line) => sum + (line.profitSellThrough ?? 0), 0)
      ),
      listingFeesMonthly: roundMoney(
        sortedLines.reduce((sum, line) => sum + line.listingFeeMonthly, 0)
      ),
      salePriceLabel: uniformPlanogramNumberLabel(
        sortedLines.map((line) => line.salePrice),
        formatPlanogramGbp
      ),
      costPriceLabel: uniformPlanogramNumberLabel(
        sortedLines.map((line) => line.costPrice),
        formatPlanogramGbp
      ),
      listingFeeLabel: uniformPlanogramNumberLabel(
        sortedLines.map((line) => line.listingFeeMonthly),
        formatPlanogramGbp
      ),
      blendedMargin: computeBlendedMargin(margins),
      sortRank: worstRank,
    };
  });

  groups.sort((a, b) =>
    comparePlanogramGroupsBySlotNumber(
      {
        earliestSlotNumber: earliestPlanogramSlotNumber(a.lines),
        empty: false,
      },
      {
        earliestSlotNumber: earliestPlanogramSlotNumber(b.lines),
        empty: false,
      }
    )
  );

  return groups;
}

/**
 * Project income at full capacity sell-through per slot (one fill cycle).
 * Listing fees and COGS are monthly; sales figures assume a single sell-through.
 */
export function computePlanogramIncome(
  slots: PlanogramIncomeSlotInput[],
  options: PlanogramIncomeOptions = {}
): {
  lines: PlanogramIncomeSlotLine[];
  groups: PlanogramIncomeProductGroup[];
  summary: PlanogramIncomeSummary;
} {
  const monthlyCogs = roundMoney(Math.max(0, options.monthlyCogs ?? 0));
  const filled = sortPlanogramDisplaySlots(
    slots.filter(
      (s) => s.productId != null && s.effectiveSell != null && s.effectiveSell > 0
    )
  );

  let listingFeesMonthly = 0;
  let revenueSellThrough = 0;
  let profitSellThrough = 0;
  let slotsMissingCost = 0;

  const lines: PlanogramIncomeSlotLine[] = filled.map((slot) => {
    const salePrice = slot.effectiveSell as number;
    const salesRevenue = roundMoney(salePrice * slot.maxCapacity);
    const listingFeeMonthly = roundMoney(Math.max(0, slot.listingFee));

    let costPrice: number | null = null;
    let marginPercent: number | null = null;
    let profit: number | null = null;

    if (slot.cost != null && slot.cost > 0) {
      const unitCost = roundMoney(slot.cost);
      costPrice = unitCost;
      marginPercent = lineMarginPercent(unitCost, salePrice);
      profit = roundMoney((salePrice - unitCost) * slot.maxCapacity);
      profitSellThrough += profit;
    } else {
      slotsMissingCost += 1;
    }

    listingFeesMonthly += listingFeeMonthly;
    revenueSellThrough += salesRevenue;

    return {
      slotId: slot.id,
      slotNumber: slot.slotNumber,
      slotLabel: slot.slotLabel,
      productId: slot.productId as string,
      productName: slot.productName,
      product: slot.product ?? null,
      maxCapacity: slot.maxCapacity,
      salePrice,
      costPrice,
      marginPercent,
      profitSellThrough: profit,
      listingFeeMonthly,
    };
  });

  const groups = groupPlanogramIncomeLines(lines);

  listingFeesMonthly = roundMoney(listingFeesMonthly);
  revenueSellThrough = roundMoney(revenueSellThrough);
  profitSellThrough = roundMoney(profitSellThrough);

  return {
    lines,
    groups,
    summary: {
      filledSlots: filled.length,
      slotsMissingCost,
      listingFeesMonthly,
      revenueSellThrough,
      profitSellThrough,
      monthlyCogs,
      blendedMargin: computeBlendedMargin(lines.map((line) => line.marginPercent)),
    },
  };
}
